/* mypka-fotoslider.js — myPKA, 21.08.2026
 *
 * Grossansicht ("Gallery View") fuer sections/mypka-fotoslider.liquid.
 *
 * Bewusst KEINE zweite Lightbox-Mechanik: <mypka-foto-lightbox> erbt von
 * ModalDialog aus global.js — derselben Basisklasse, auf der die
 * Produktgalerie (<product-modal>) sitzt. Damit kommen Escape, Fokusfalle,
 * Fokusrueckgabe an den Oeffner und die Scroll-Sperre am Body aus dem Theme
 * und verhalten sich identisch zum Rest des Shops.
 *
 * Ergaenzt werden nur drei Dinge, die das Theme nicht mitbringt:
 *   1. Pfeiltasten-Bedienung (Swiper-Keyboard-Modul, nur waehrend offen),
 *   2. eine zweite Fokus-Schicht: `trapFocus()` aus global.js erhebt die
 *      Fokusliste EINMAL beim Oeffnen (Journal 20.08.). Fuer diesen Dialog
 *      ist die Liste statisch, aber die Pfeiltasten koennen `disabled`
 *      werden — deshalb holt ein `focusin` am Dokument den Fokus zurueck,
 *      falls er doch herausrutscht,
 *   3. Klick neben das Bild schliesst — als Zeiger-Paarung, nicht als
 *      `click`: Druecken und Loslassen muessen auf demselben Element
 *      liegen, sonst zaehlt ein Wisch am Touchgeraet als Aussenklick
 *      (Journal 20.08.).
 */
(() => {
	const KLICK_TOLERANZ = 10;

	// ModalDialog kommt aus global.js (defer, im <head>). Deferred Scripts
	// laufen in Dokumentreihenfolge — die Klasse steht hier also. Trotzdem
	// eine Wache: ohne sie waere ein Ladefehler in global.js ein stiller
	// Totalausfall dieser Sektion.
	if (typeof ModalDialog === 'undefined') {
		console.warn('mypka-fotoslider: ModalDialog aus global.js fehlt — Grossansicht deaktiviert.');
		return;
	}

	if (!window.customElements.get('mypka-foto-lightbox')) {
		class MypkaFotoLightbox extends ModalDialog {
			constructor() {
				super();
				this.swiper = null;
				this.vorbereitet = false;

				// Fokus-Rueckholung (Schicht 2 zur Fokusfalle aus global.js)
				this.aufFocusin = (event) => {
					if (!this.hasAttribute('open')) return;
					if (this.contains(event.target)) return;
					this.dialog?.focus();
				};

				// Klick neben das Bild schliesst — Paarung statt `click`.
				this.klickStart = null;
				this.addEventListener('pointerdown', (event) => {
					this.klickStart = this.istHintergrund(event.target)
						? { ziel: event.target, x: event.clientX, y: event.clientY }
						: null;
				});
				this.addEventListener('pointercancel', () => {
					this.klickStart = null;
				});
				this.addEventListener('pointerup', (event) => {
					const start = this.klickStart;
					this.klickStart = null;
					if (!start || start.ziel !== event.target) return;
					if (Math.abs(event.clientX - start.x) > KLICK_TOLERANZ) return;
					if (Math.abs(event.clientY - start.y) > KLICK_TOLERANZ) return;
					this.hide();
				});
			}

			/* Kindelemente erst NACH dem Upgrade lesen, nie im Konstruktor —
			 * dort duerfen sie laut Spezifikation noch nicht existieren. */
			vorbereiten() {
				if (this.vorbereitet) return;
				this.dialog = this.querySelector('[role="dialog"]');
				this.zaehler = this.querySelector('.mypka-fotoslider__lightbox-zaehler');
				this.zurueck = this.querySelector('.mypka-fotoslider__lightbox-taste--zurueck');
				this.vor = this.querySelector('.mypka-fotoslider__lightbox-taste--vor');
				this.anzahl = this.querySelectorAll('.mypka-fotoslider__lightbox-slide').length;
				this.zurueck?.addEventListener('click', () => this.swiper?.slidePrev());
				this.vor?.addEventListener('click', () => this.swiper?.slideNext());
				this.vorbereitet = true;
			}

			/* Hintergrund = die Slide-Flaeche, nicht das Foto und nicht die Bedienung. */
			istHintergrund(el) {
				if (!(el instanceof Element)) return false;
				if (el.closest('button, a')) return false;
				if (el.tagName === 'IMG') return false;
				return Boolean(el.closest('.mypka-fotoslider__lightbox-slide, .mypka-fotoslider__lightbox-slider'));
			}

			starteSwiper() {
				if (this.swiper || typeof Swiper === 'undefined') return;
				const el = this.querySelector('.mypka-fotoslider__lightbox-slider');
				if (!el) return;
				this.swiper = new Swiper(el, {
					slidesPerView: 1,
					spaceBetween: 24,
					loop: false,
					speed: 400,
					a11y: {
						enabled: true,
						prevSlideMessage: 'Vorheriges Foto',
						nextSlideMessage: 'Nächstes Foto',
						slideLabelMessage: 'Foto {{index}} von {{slidesLength}}',
					},
					keyboard: { enabled: false, onlyInViewport: false },
					on: {
						slideChange: () => this.zeigeStand(),
						init: () => this.zeigeStand(),
					},
				});
			}

			zeigeStand() {
				const i = (this.swiper?.activeIndex ?? 0) + 1;
				if (this.zaehler) this.zaehler.textContent = `${i} / ${this.anzahl}`;
				if (this.zurueck) this.zurueck.disabled = Boolean(this.swiper?.isBeginning);
				if (this.vor) this.vor.disabled = Boolean(this.swiper?.isEnd);
			}

			zeige(index, opener) {
				this.vorbereiten();
				this.starteSwiper();
				super.show(opener);
				document.addEventListener('focusin', this.aufFocusin);
				// Swiper misst eine verborgene Flaeche falsch — nach dem Oeffnen
				// neu vermessen, sonst steht der Slider auf der falschen Karte
				// (Journal 21.08.: „ein Swiper luegt bis zum update").
				requestAnimationFrame(() => {
					this.swiper?.update();
					this.swiper?.slideTo(index, 0);
					this.swiper?.keyboard?.enable();
					this.zeigeStand();
				});
			}

			hide() {
				document.removeEventListener('focusin', this.aufFocusin);
				this.swiper?.keyboard?.disable();
				super.hide();
			}
		}

		window.customElements.define('mypka-foto-lightbox', MypkaFotoLightbox);
	}

	/* Modus "Original" (image_ratio = mixed): unterschiedlich breite Karten.
	 *
	 * Der Theme-Slider card-slider.js kann das nicht - er setzt `slidesPerView`
	 * auf feste Zahlen, also gleich breite Slides. Statt die Theme-Datei zu
	 * gabeln (sections/card-slider.liquid benutzt sie mit), belegen wir den
	 * Slider hier zuerst: `initCardSliders` steigt aus, sobald der Wrapper
	 * `slider-initialized` traegt, und unser <script> steht in der Sektion
	 * VOR dem Theme-Skript. Deferred Scripts laufen in Dokumentreihenfolge.
	 *
	 * Uebernommen werden Gassen, Pfeile, Pagination und Tempo aus dem Theme,
	 * damit sich der Slider anfuehlt wie jeder andere im Shop. Einziger
	 * Unterschied: `slidesPerView: 'auto'` - die Breite kommt aus dem CSS,
	 * wo sie sich aus Kartenhoehe x Seitenverhaeltnis ergibt. */
	const initMixedSlider = (abschnitt) => {
		if (!abschnitt || !abschnitt.classList?.contains('card-slider-section')) return;

		const wrapper = abschnitt.querySelector('.card-slider__wrapper');
		if (!wrapper || wrapper.dataset.bildformat !== 'mixed') return;
		if (wrapper.classList.contains('slider-initialized')) return;

		const container = wrapper.querySelector('.swiper');
		if (!container || typeof Swiper === 'undefined') return;

		const optionen = {
			slidesPerView: 'auto',
			spaceBetween: 8,
			loop: false,
			speed: 800,
			mousewheel: { forceToAxis: true },
			breakpoints: {
				750: { spaceBetween: 14 },
			},
			pagination: {
				el: wrapper.querySelector('.card-slider__pagination'),
				clickable: true,
				type: 'custom',
				renderCustom: (swiper, current, total) => {
					let out = '';
					for (let i = 1; i < total + 1; i++) {
						const aktiv = i === current ? ' swiper-pagination-bullet-active' : '';
						out += `<span class="swiper-pagination-bullet${aktiv}" tabindex="0" role="button" aria-label="Gehe zu Bildausschnitt ${i}"></span>`;
					}
					return out;
				},
			},
		};

		const vor = wrapper.querySelector('.card-slider__button--next');
		const zurueck = wrapper.querySelector('.card-slider__button--prev');
		if (vor && zurueck) {
			optionen.navigation = { nextEl: vor, prevEl: zurueck };
		}

		new Swiper(container, optionen);
		// Erst NACH dem erfolgreichen Start markieren: bricht Swiper vorher ab,
		// soll card-slider.js noch uebernehmen duerfen - lieber gleich breite
		// Karten als gar kein Slider.
		wrapper.classList.add('slider-initialized');
	};

	/* ===== Punktfenster mobil — myPKA 28.08.2026 (Felix), Ticket 869eqz4br =====
	 *
	 * BEFUND M10: 25 Fotos, 24 Punkte. Die Leiste ist `display:flex` mit
	 * 0,8 rem Abstand und 350 px breit — 24 Punkte à 24 px passen nicht hinein,
	 * flexbox schrumpft sie auf 6 x 4 px (aktiver 21 x 4). Gemessen bei 390 px
	 * und 320 px. WCAG 2.2 SC 2.5.8 verlangt 24 x 24 px; die Abstandsausnahme
	 * greift bei 6 px Mittenabstand nicht.
	 *
	 * WELCHER SLIDER HIER UEBERHAUPT LAEUFT — nachgesehen, nicht angenommen:
	 * Diese Sektion steht auf `image_ratio: portrait`, NICHT auf `mixed`.
	 * `initMixedSlider` weiter oben steigt deshalb aus, und den Swiper baut
	 * assets/card-slider.js — eine Theme-Datei, die sections/card-slider.liquid
	 * mitbenutzt. Sie wird hier bewusst NICHT geaendert (dieselbe Begruendung
	 * wie im Kommentar der Sektion, 25.08.2026).
	 *
	 * Stattdessen wird die vorhandene `renderCustom` UMHUELLT:
	 *   - ab 750 px ruft die Huelle die Originalfunktion auf. Das erzeugte
	 *     Markup ist damit Zeichen fuer Zeichen dasselbe wie vorher — der
	 *     Desktop ist nachweislich unberuehrt, nicht nur vermutlich.
	 *   - darunter rendert sie ein Fenster von hoechstens sieben Punkten.
	 *
	 * WARUM ALLE PUNKTE GERENDERT UND NUR AUSGEBLENDET WERDEN:
	 * Swipers Klickbehandlung rechnet den Zielslide aus der DOM-Position des
	 * Punktes (`$(this).index() * slidesPerGroup`, swiper-bundle.min.js). Wer
	 * die Punkte ausserhalb des Fensters gar nicht erst erzeugt, verschiebt
	 * still jeden Klickziel-Index. Ausgeblendete Punkte behalten ihre Position,
	 * fallen aber ueber `display:none` aus Tabreihenfolge und Screenreader-Lauf.
	 * Die Ausblendung und die 24-px-Flaeche stehen in assets/mypka-mobil.css,
	 * Abschnitt 4.
	 *
	 * ZEITPUNKT: card-slider.js laeuft direkt nach dieser Datei (beide `defer`,
	 * Dokumentreihenfolge). Die Swiper-Instanz steht also erst danach. Der
	 * Einstiegspunkt ist deshalb DOMContentLoaded — das ist keine Wartezeit auf
	 * gut Glueck, sondern eine Zusicherung der Spezifikation: alle `defer`-
	 * Skripte sind vor diesem Ereignis abgearbeitet. */
	const PUNKT_UMSCHALTPUNKT = 749;
	const PUNKTFENSTER = 7;

	const istPunktMobil = () =>
		window.matchMedia(`(max-width: ${PUNKT_UMSCHALTPUNKT}px)`).matches;

	// `total` ist die Zahl der Swiper-Schritte, NICHT die der Fotos (25 Fotos,
	// 2 Karten je Schritt -> 24 Schritte). Deshalb steht sie bewusst nicht im
	// Screenreader-Text: „Bildausschnitt 3 von 24" waere eine falsche Auskunft
	// ueber die Menge der Bilder.
	const punkteMobilRendern = (current, total) => {
		let von = 1;
		let bis = total;
		if (total > PUNKTFENSTER) {
			const halb = Math.floor(PUNKTFENSTER / 2);
			von = Math.min(Math.max(1, current - halb), total - PUNKTFENSTER + 1);
			bis = von + PUNKTFENSTER - 1;
		}
		let out = '';
		for (let i = 1; i < total + 1; i++) {
			const aktiv = i === current ? ' swiper-pagination-bullet-active' : '';
			const aus = i < von || i > bis ? ' mypka-punkt--aus' : '';
			out += `<span class="swiper-pagination-bullet mypka-punkt${aktiv}${aus}" tabindex="0" role="button" aria-label="Gehe zu Bildausschnitt ${i}"><span class="mypka-punkt__kern"></span></span>`;
		}
		return out;
	};

	const punktfensterAnhaengen = (abschnitt) => {
		if (!abschnitt || !abschnitt.classList?.contains('mypka-fotoslider-section')) return;
		const container = abschnitt.querySelector('.mypka-fotoslider__wrapper .swiper');
		const swiper = container && container.swiper;
		if (!swiper || swiper.mypkaPunktfenster) return;
		const pagination = swiper.params?.pagination;
		if (!pagination || pagination.type !== 'custom' || typeof pagination.renderCustom !== 'function') return;

		const original = pagination.renderCustom;
		pagination.renderCustom = (s, current, total) =>
			istPunktMobil() ? punkteMobilRendern(current, total) : original(s, current, total);
		swiper.mypkaPunktfenster = true;

		// Das Fenster folgt dem aktiven Punkt ueber `pagination.update()`, das
		// Swiper bei jedem Slidewechsel selbst aufruft. Beim Wechsel der Breite
		// (Drehen des Geraets, Desktop <-> Mobil) tut es das nicht.
		swiper.on('resize', () => swiper.pagination?.update());
		swiper.pagination?.update();
	};

	const initFotoslider = (abschnitt) => {
		if (!abschnitt || !abschnitt.classList) return;
		const wrapper = abschnitt.querySelector('.mypka-fotoslider__wrapper');
		if (!wrapper || wrapper.dataset.lightboxInitialisiert === 'true') return;

		const lightboxId = wrapper.dataset.lightboxId;
		if (!lightboxId) return;

		// ModalDialog.connectedCallback haengt den Dialog an <body>. Beim
		// Neuladen einer Sektion im Editor bliebe der alte sonst liegen.
		const kandidaten = document.querySelectorAll(`mypka-foto-lightbox[id="${CSS.escape(lightboxId)}"]`);
		const lightbox = kandidaten[kandidaten.length - 1];
		if (!lightbox) return;
		kandidaten.forEach((el) => {
			if (el !== lightbox) el.remove();
		});

		wrapper.querySelectorAll('.mypka-fotoslider__karte').forEach((karte) => {
			karte.addEventListener('click', () => {
				const index = Number.parseInt(karte.dataset.fotoIndex, 10) || 0;
				lightbox.zeige(index, karte);
			});
		});

		wrapper.dataset.lightboxInitialisiert = 'true';
	};

	const abschnittHier = document.currentScript?.parentElement;
	initMixedSlider(abschnittHier);
	initFotoslider(abschnittHier);

	/* Ticket 869eqz4br: erst nachdem auch card-slider.js gelaufen ist.
	 *
	 * FALLE, in die dieser Lauf zuerst getappt ist: waehrend ein `defer`-Skript
	 * laeuft, steht `document.readyState` bereits auf 'interactive' — nicht auf
	 * 'loading'. Die uebliche Abfrage `readyState === 'loading' ? warten :
	 * sofort` fuehrt hier also in den SOFORT-Zweig, und dort gibt es die
	 * Swiper-Instanz von card-slider.js noch gar nicht. Gemessen: Punkte
	 * unveraendert 24 x 6 px.
	 *
	 * Deshalb beide Wege, und die Funktion ist idempotent
	 * (`swiper.mypkaPunktfenster`): DOMContentLoaded ist der zugesicherte
	 * Zeitpunkt nach allen `defer`-Skripten, der Task danach faengt den Fall
	 * ab, dass das Ereignis schon durch war. */
	document.addEventListener('DOMContentLoaded', () => punktfensterAnhaengen(abschnittHier), { once: true });
	setTimeout(() => punktfensterAnhaengen(abschnittHier), 0);

	document.addEventListener('shopify:section:load', (event) => {
		initMixedSlider(event.target);
		initFotoslider(event.target);
		// Im Theme-Editor laeuft card-slider.js sein eigenes section:load erst
		// NACH diesem hier (Reihenfolge der Registrierung = Skriptreihenfolge).
		// Ein Task spaeter steht die neue Instanz.
		setTimeout(() => punktfensterAnhaengen(event.target), 0);
	});
})();
